// src/components/block/liveStreamManagement/ControlBar.tsx
// v8 — Switch audio :
//      • Mode "Source" → mutes restaurés selon SourceAudioConfig (comportement normal)
//      • Mode "Fixe"   → toutes les sources du mixer mutées sauf la source fixe
//      • Le select liste TOUTES les sources audio détectées, y compris celles qui
//        n'occupent aucun emplacement du deck : le parent les branche à la demande
//        (cf. LiveStreamManager, sources fixes autonomes). Les périphériques audio
//        système sont scindés en deux groupes selon qu'ils sont rattachés au deck
//        ou non ; les autres familles gardent leur groupe d'origine.
//      • L'état est remonté au parent via onAudioSwitchChange (AudioSwitchState)

import React, { useState, useMemo, useEffect } from 'react';
import { Settings, Plus, Circle, Save, FolderOpen, Clapperboard, SlidersHorizontal, Volume2, VolumeX } from 'lucide-react';
import { LUFS_TARGET, setVuMeterMode, useVuMeterMode } from './vuMeterZones';

import { RemoteDevice, RemoteConnection, DeviceAuthMode } from '../../../dataTypes/liveStreamManagment/types_webrtc';
import { RegisteredRemoteDevice } from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { SourceAliasMap } from './SourceAliasConfigModal';

import AnimatedWaitingIcon from '../../widget/animations/animatedWaitingIcon';
import { Icon }            from '../../widget/images/Icon';
import Toggle              from '../../widget/inputs/Toggle';

import ProductionControlRoom_RemoteDevicesPanel from './ProductionControlRoom_RemoteDevicesPanel';
import MixerStrip from './MixerStrip';
import AutoDirectorSettingsModal, { AutoDirectorCandidate } from './AutoDirectorSettingsModal';
import WhipEncodingSettingsModal from './WhipEncodingSettingsModal';
import { useWhipEncodingSettings } from './useWhipEncodingSettings';
import { AutoDirectorSettings } from './autoDirector';
import { AutoDirectorStatus } from './useAutoDirector';

import { VideoSource } from '../../../contexts/MediaStreamContext';
import { HlsProbeStatus } from './useHlsProber';
import { formatRemainingDuration } from '../../../backendServices/liveStreamManagment/api_recording';
import { updateBroadcastingChannelMultiview, updateBroadcastingChannelPublicPortal, getXtreamConnectionCount } from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { useStreamDeckControl } from '../../../dataTypes/liveStreamManagment/streamDeckBridge';
import SubtitlesControl from './SubtitlesControl';


function formatElapsed(totalSec: number): string {
  const h  = Math.floor(totalSec / 3600);
  const m  = Math.floor((totalSec % 3600) / 60);
  const s  = totalSec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}‍️\s]+/u;
function cleanLabel(raw: string): string { return raw.replace(EMOJI_RE, '').trim(); }

// ── Types publics ─────────────────────────────────────────────────────────────

/**
 * État du switch audio.
 * - mode === 'source' → l'audio WHIP suit la source vidéo finale (comportement par défaut)
 * - mode === 'fixe'   → toutes les sources du mixer sont mutées sauf fixedAudioSourceId
 * - mode === 'mix'    → plusieurs sources du deck mélangées, chacune au niveau de mixGains
 *
 * Format de fixedAudioSourceId :
 *   "dev:<deviceId>"      → périphérique audio système
 *   "remote:<device_id>"  → source distante WebRTC
 */
export interface AudioSwitchState {
  mode               : 'source' | 'fixe' | 'mix';
  fixedAudioSourceId : string;
  /** Mode 'mix' : gain linéaire par id d'option (absent = 1, soit 0 dB). */
  mixGains          ?: Record<string, number>;
  /** Mode 'mix' : voies coupées. Le gain est conservé, simplement ignoré. */
  mixMuted          ?: Record<string, boolean>;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ControlBarProps {
  hasCurrentStream     : boolean;
  isStreaming          : boolean;
  isStreamButtonActive : boolean;
  isStartingStream     : boolean;
  isStoppingStream     : boolean;
  onStartStreaming     : () => void;
  handleStopStreaming  : () => void;
  isRecording          : boolean;
  isTogglingRecording  : boolean;
  recordingElapsedSec  : number;
  // Estimation du temps d'enregistrement restant avant d'atteindre le quota
  // disque de l'admin (secondes). null = pas d'estimation possible (admin non
  // identifié ou débit inconnu) — cf. estimateRemainingRecordingSeconds.
  remainingRecordingSec?: number | null;
  onStartRecording     : () => void;
  onStopRecording      : () => void;
  onOpenStorage        : () => void;
  isRegiePowered       : boolean;
  // Config deck
  onOpenPupitreConfig    : () => void;
  onOpenMediaboardConfig : () => void;
  onOpenDeckFullscreen   : () => void;
  // Invites (remote devices)
  remoteDevices        : RemoteDevice[];
  remoteConnections    : Map<string, RemoteConnection>;
  onRegisterDevice     : (id: string, label: string, password: string, authMode?: DeviceAuthMode, options?: { silent?: boolean; accessToken?: string }) => Promise<RegisteredRemoteDevice | undefined>;
  onDeleteDevice       : (device_id: string) => void;
  onDisconnectDevice   : (device_id: string) => void;
  onRegeneratePassword : (device_id: string) => void;
  onSetDirector        : (device_id: string, active: boolean) => void;
  directorDeviceIds    : Set<string>;
  deviceNetworkQuality : Map<string, { level: number; sample: any }>;
  onLoadDevices        : () => void;
  onRegisterXtreamConnection : (url: string, xtream?: XtreamCredentials, label?: string, opts?: { autoRetry?: boolean }) => void;
  serverUrl                  : string;
  streamPath                 : string | null;   // null = aucune chaîne identifiée, aucun repli
  webRtcPageUrl              : string;
  channelName                : string;
  programOrientation?        : 'landscape' | 'portrait';
  microserviceUrl            : string;
  // Alias
  sourceAliases   : SourceAliasMap;
  onAliasesChange : (aliases: SourceAliasMap) => void;
  // Regie
  onSaveConfig : () => void;
  onLoadConfig : () => void;

  // ── Sources audio pour le select (optionnel) ─────────────────────────────
  // Périphériques audio système — même liste qu'utilisée dans SourceConfigModal.
  // Format : deviceId = l'ID natif du périphérique (pas préfixé).
  availableAudioDevices ?: { deviceId: string; label: string; kind?: string }[];

  // ── Contrôle du switch audio depuis le parent (optionnel) ────────────────
  // En mode non-contrôlé (props absents), l'état est géré localement.
  audioSwitchState    ?: AudioSwitchState;
  onAudioSwitchChange ?: (state: AudioSwitchState) => void;

  // Ids d'options (même format que fixedAudioSourceId) déjà rattachées à un slot
  // du deck, donc présentes dans l'AudioSourceRegistry. Sert uniquement à répartir
  // la liste en deux groupes : toutes les options restent sélectionnables, le
  // parent branche à la demande celles qui ne sont pas dans le deck.
  // Non fourni = tout est présenté comme rattaché (mode non contrôlé).
  deckAttachedAudioIds ?: string[];
  // Id d'option → id du slot du deck (lecture du niveau mesuré, VU-mètres du Mixeur).
  mixSourceIds ?: Record<string, string>;
  /** Brouillon du Mixeur ouvert (null = fermé) : seule l'écoute locale l'entend avant validation. */
  onMixerDraftChange ?: (draft: { gains: Record<string, number>; muted: Record<string, boolean>; monitorMuted?: boolean } | null) => void;
  mixMeterRegistry ?: import('../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry').AudioSourceRegistry;

  // Ancien prop conserve pour compatibilite
  onOpenDevicesPanel ?: () => void;
  
   availableSources  : (VideoSource | null)[];
  hlsProbeStatusMap : Map<string, HlsProbeStatus>;
  
  selectedVideoSource  : string;
  deckPreviewSourceId  : string | null;
  onRemoveSource       : (sourceId: string) => void;  
  /** Relance d'un flux HLS/Xtream depuis le panneau des sources. */
  onReloadSource       : (sourceId: string) => Promise<void>;
  /** Renommage d'affichage d'une source, sans toucher au flux. */
  onRenameSource       : (sourceId: string, label: string) => void;
  channelActive : boolean;
  channelFullyActive : boolean;
  // Distinct de channelFullyActive : vrai dès que la chaîne est active côté serveur,
  // sans attendre le signal HLS retour. Tant qu'elle ne l'est pas, Rec est désactivé.
  channelReadyForRecording : boolean;

  // Multi-angle / multivue : persistée en BDD (BroadcastingChannel.multiview) et
  // exposée telle quelle au player Xtream (get_live_streams).
  broadcastingChannelId : number;
  multiview             : boolean;
  // Remonte la bascule au parent : `broadcastingChannel` n'est pas rechargé après le
  // PATCH, or la boîte "Enregistrement d'une session" doit connaître le mode courant.
  onMultiviewChange    ?: (value: boolean) => void;
  // Chaîne listée sur Kleon.tv (BroadcastingChannel.publicPortal) : seulement pour
  // la touche « Kleon.tv » du Stream Deck, la barre n'a pas ce bouton.
  publicPortal         ?: boolean;

  // ── Réalisateur auto ──────────────────────────────────────────────────────
  // Optionnel : absent, la section n'est pas affichée.
  autoDirectorEnabled     ?: boolean;
  onAutoDirectorToggle    ?: (on: boolean) => void;
  autoDirectorStatus      ?: AutoDirectorStatus;
  autoDirectorSettings    ?: AutoDirectorSettings;
  onAutoDirectorSettingsChange ?: (next: AutoDirectorSettings) => void;
  autoDirectorCandidates  ?: AutoDirectorCandidate[];
}

// ── Helpers de mise en forme ──────────────────────────────────────────────────

const Sep: React.FC = () => (
  <div style={{
    width: 1, margin: '10px 6px',
    background: 'rgba(255,255,255,0.12)',
    flexShrink: 0, alignSelf: 'stretch',
  }} />
);

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', flexShrink: 0, padding: '0 6px',
  }}>
    <div style={{
      height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 700, letterSpacing: '0.7px',
      color: '#FFFFFF', textTransform: 'uppercase',
      whiteSpace: 'nowrap', width: '100%',
    }}>
      {label}
    </div>
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 4, paddingBottom: 8,
    }}>
      {children}
    </div>
  </div>
);

const BTN: React.CSSProperties = {
  height: 44, padding: '0 11px', borderRadius: 7,
  background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#FFFFFF', fontSize: 11, fontFamily: 'monospace',
  fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: 6, whiteSpace: 'nowrap', boxSizing: 'border-box', flexShrink: 0,
};

const BTN_ICON: React.CSSProperties = { ...BTN, width: 44, padding: 0 };

// ── Composant principal ───────────────────────────────────────────────────────

const ControlBar: React.FC<ControlBarProps> = ({
  hasCurrentStream, isStreaming, isStreamButtonActive,
  isStartingStream, isStoppingStream,
  onStartStreaming, handleStopStreaming,
  isRecording, isTogglingRecording, recordingElapsedSec, remainingRecordingSec,
  onStartRecording, onStopRecording, onOpenStorage,
  isRegiePowered,
  onOpenPupitreConfig, onOpenMediaboardConfig, onOpenDeckFullscreen,
  remoteDevices, remoteConnections,
  onRegisterDevice, onDeleteDevice, onDisconnectDevice, onRegeneratePassword, onSetDirector, directorDeviceIds, deviceNetworkQuality, onLoadDevices,
  onRegisterXtreamConnection,
  serverUrl, streamPath, webRtcPageUrl, channelName, programOrientation,
  microserviceUrl,
  sourceAliases, onAliasesChange,
  onSaveConfig, onLoadConfig,
  availableAudioDevices = [],
  audioSwitchState      : controlledState,
  mixSourceIds,
  onMixerDraftChange,
  mixMeterRegistry,
  onAudioSwitchChange,
  deckAttachedAudioIds,
  availableSources,
  hlsProbeStatusMap,
  selectedVideoSource,
  deckPreviewSourceId,
  onRemoveSource,
  onReloadSource,
  onRenameSource,
  channelActive,
  channelFullyActive,
  channelReadyForRecording,
  broadcastingChannelId,
  multiview,
  onMultiviewChange,
  publicPortal = false,
  autoDirectorEnabled = false,
  onAutoDirectorToggle,
  autoDirectorStatus = 'off',
  autoDirectorSettings,
  onAutoDirectorSettingsChange,
  autoDirectorCandidates = [],
}) => {
  const isStreamBusy      = isStartingStream || isStoppingStream;
  const isRecordingBusy   = isTogglingRecording;
  // Rec exige une chaîne active (channelReadyForRecording). Si un enregistrement est déjà en
  // cours, on ne bloque jamais son arrêt : sinon la chaîne qui se désactive pendant
  // qu'on enregistre rendrait le bouton Stop inaccessible.
  const canStartRecording = channelReadyForRecording && hasCurrentStream && isRegiePowered;
  const recordingDisabled = isRecordingBusy || (!isRecording && !canStartRecording);
  
  // ── Décompte des sources externes ─────────────────────────────────────────
  //
  // Toutes les sources venues du dehors, et pas seulement les téléphones : flux
  // HLS libres, chaînes Xtream et caméras RTMP en font partie, et l'opérateur qui
  // lit « 2/3 » veut savoir combien de ses ENTRÉES répondent, quelle que soit la
  // technique qui les amène.
  //
  // Sont exclues les deux sources qui ne viennent pas du dehors mais de la chaîne
  // elle-même : sa playlist (`hls-video-…`) et son entrée OBS/RTMP
  // (`hls-live-…`). Les compter reviendrait à s'annoncer à soi-même.
  //
  // « Connectée » n'a pas le même sens des deux côtés, et c'est assumé : pour un
  // téléphone c'est une PeerConnection ouverte, pour un flux distant c'est une
  // sonde HLS qui répond. Dans les deux cas, c'est ce que l'opérateur appelle
  // « ça arrive ».
  const hlsSources = availableSources.filter(
    (s): s is VideoSource =>
      s !== null &&
      s.type === 'url' &&
      s.id !== `hls-video-${streamPath}` &&
      s.id !== `hls-live-${streamPath}`
  );
  const connectedCount = remoteDevices.filter(d => remoteConnections.has(d.device_id)).length
                       + hlsSources.filter(s => hlsProbeStatusMap.get(s.id) === 'ok').length;
  const totalCount     = remoteDevices.length + hlsSources.length;

  const [showAddModal,    setShowAddModal]    = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAutoDirectorSettings, setShowAutoDirectorSettings] = useState(false);
  const [showWhipEncodingSettings, setShowWhipEncodingSettings] = useState(false);
  const whipEncoding = useWhipEncodingSettings(broadcastingChannelId);

  // ── Multi-angle / multivue — persisté sur BroadcastingChannel.multiview ──
  const [multiAngleActive,    setMultiAngleActive]    = useState(multiview);
  const [multiAngleToggling,  setMultiAngleToggling]  = useState(false);

  useEffect(() => { setMultiAngleActive(multiview); }, [multiview, broadcastingChannelId]);

  const handleToggleMultiAngle = () => {
    const nextValue = !multiAngleActive;
    const rollback  = () => { setMultiAngleActive(!nextValue); onMultiviewChange?.(!nextValue); };

    setMultiAngleToggling(true);
    setMultiAngleActive(nextValue);
    onMultiviewChange?.(nextValue);

    updateBroadcastingChannelMultiview(broadcastingChannelId, nextValue)
      .then(result => {
        if (result.code !== 1) { rollback(); }
      })
      .catch(rollback)
      .finally(() => { setMultiAngleToggling(false); });
  };

  // ── Stream Deck (protocole 3, 2026-10-16) ──────────────────────────────────
  // Les touches On air, Rec, Multi view, Pupitre, Média et scène et Kleon.tv
  // reprennent les boutons de cette barre, avec leurs règles : une touche ne
  // fait que ce qu'un clic ferait. Le plugin n'envoie l'arrêt de On air et Rec
  // qu'après un appui long ; ici, `stop` ne fait qu'arrêter et `start` que
  // démarrer.
  const canStartStreamingDeck = channelFullyActive && hasCurrentStream && isRegiePowered;
  useStreamDeckControl('onAir', {
    available: isStreamButtonActive || canStartStreamingDeck,
    active:    isStreamButtonActive,
    busy:      isStreamBusy,
    detail:    null,
  }, cmd => {
    if (isStreamBusy) return;
    if (cmd.action === 'stop' && isStreamButtonActive) handleStopStreaming();
    if (cmd.action === 'start' && !isStreamButtonActive && canStartStreamingDeck) onStartStreaming();
  });

  useStreamDeckControl('rec', {
    available: isRecording || canStartRecording,
    active:    isRecording,
    busy:      isRecordingBusy,
    detail:    isRecording ? formatElapsed(recordingElapsedSec) : null,
  }, cmd => {
    if (isRecordingBusy) return;
    if (cmd.action === 'stop' && isRecording) onStopRecording();
    if (cmd.action === 'start' && !isRecording && canStartRecording) onStartRecording();
  });

  useStreamDeckControl('multiview', {
    available: !multiAngleToggling,
    active:    multiAngleActive,
    busy:      multiAngleToggling,
    detail:    null,
  }, cmd => {
    if (multiAngleToggling) return;
    const next = cmd.action === 'toggle' ? !multiAngleActive : cmd.action === 'start';
    if (next !== multiAngleActive) handleToggleMultiAngle();
  });

  useStreamDeckControl('pupitre', { available: true, active: false, busy: false, detail: null }, () => onOpenPupitreConfig());
  useStreamDeckControl('media',   { available: true, active: false, busy: false, detail: null }, () => onOpenMediaboardConfig());

  // « Visible sur Kleon.tv » : même appel que la fiche de la chaîne. L'état suit
  // la chaîne rechargée par le parent ; la touche le change tout de suite.
  const [portalVisible, setPortalVisible] = useState(publicPortal);
  const [portalBusy,    setPortalBusy]    = useState(false);
  useEffect(() => { setPortalVisible(publicPortal); }, [publicPortal, broadcastingChannelId]);
  useStreamDeckControl('portal', {
    available: broadcastingChannelId > 0 && !portalBusy,
    active:    portalVisible,
    busy:      portalBusy,
    detail:    null,
  }, cmd => {
    if (portalBusy || !(broadcastingChannelId > 0)) return;
    const next = cmd.action === 'toggle' ? !portalVisible : cmd.action === 'start';
    if (next === portalVisible) return;
    setPortalBusy(true);
    setPortalVisible(next);
    updateBroadcastingChannelPublicPortal(broadcastingChannelId, next)
      .then(result => { if (result.code !== 1) setPortalVisible(!next); })
      .catch(() => setPortalVisible(!next))
      .finally(() => setPortalBusy(false));
  });

  // Spectateurs : le compteur que montrent Kleon.tv et la fiche de la chaîne
  // (connexions au flux de la dernière minute, xtream_connection_count), relevé
  // toutes les 15 s chaîne allumée. Seulement pour la touche du Stream Deck.
  const [viewerCount, setViewerCount] = useState<number | null>(null);
  const [viewerTick,  setViewerTick]  = useState(0);
  useEffect(() => {
    if (!channelActive || !(broadcastingChannelId > 0)) { setViewerCount(null); return; }
    let cancelled = false;
    const fetchCount = async () => {
      const result = await getXtreamConnectionCount(broadcastingChannelId);
      if (!cancelled && result.code === 1) setViewerCount(result.payload.activeConnections);
    };
    void fetchCount();
    const timer = setInterval(fetchCount, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [channelActive, broadcastingChannelId, viewerTick]);
  useStreamDeckControl('viewers', {
    available: channelActive,
    active:    (viewerCount ?? 0) > 0,
    busy:      false,
    detail:    viewerCount !== null ? String(viewerCount) : null,
  }, () => setViewerTick(t => t + 1));

  // ── État local (mode non-contrôlé) ───────────────────────────────────────
  const [internalState, setInternalState] = useState<AudioSwitchState>({
    mode: 'source', fixedAudioSourceId: '',
  });

  const [showMixer, setShowMixer] = useState(false);
  const mixerBtnRef = React.useRef<HTMLButtonElement>(null);
  const vuMode      = useVuMeterMode();

  const isControlled  = controlledState !== undefined;
  const audioSwitch   = isControlled ? controlledState! : internalState;
  const audioMode     = audioSwitch.mode;
  const fixedSourceId = audioSwitch.fixedAudioSourceId;

  const mixerVisible = audioMode === 'mix' && showMixer;
  // Brouillon : modifié par le Mixeur, appliqué à la diffusion au clic sur Valider.
  const [mixDraft, setMixDraft] = useState<{ gains: Record<string, number>; muted: Record<string, boolean>; monitorMuted?: boolean }>({ gains: {}, muted: {} });
  useEffect(() => {
    onMixerDraftChange?.(mixerVisible ? mixDraft : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixerVisible, mixDraft]);
  useEffect(() => () => { onMixerDraftChange?.(null); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);




  const emitChange = (next: AudioSwitchState) => {
    if (!isControlled) setInternalState(next);
    onAudioSwitchChange?.(next);
  };

const handleModeChange = (mode: AudioSwitchState['mode']) => {
  if (mode === 'source') {
    setShowMixer(false);
    emitChange({ mode: 'source', fixedAudioSourceId: '' });
    return;
  }
  if (mode === 'mix') {
    emitChange({ mode: 'mix', fixedAudioSourceId: '', mixGains: audioSwitch.mixGains ?? {}, mixMuted: audioSwitch.mixMuted ?? {} });
    return;
  }
  setShowMixer(false);
  // En passant en mode "Fixe", on présélectionne de préférence une source déjà
  // rattachée au deck : elle est audible immédiatement, là où une source non
  // rattachée doit d'abord être acquise (et peut demander une autorisation micro).
  const defaultId = (allOptions.find(o => isDeckAttached(o.id)) ?? allOptions[0])?.id ?? '';
  emitChange({ mode: 'fixe', fixedAudioSourceId: defaultId });
};

  const handleMixGainChange = (optionId: string, gain: number) =>
    setMixDraft(d => ({ ...d, gains: { ...d.gains, [optionId]: gain } }));

  const handleMixMutedSet = (optionId: string, isMuted: boolean) =>
    setMixDraft(d => ({ ...d, muted: { ...d.muted, [optionId]: isMuted } }));

  const openMixer = () => {
    setMixDraft({ gains: { ...(audioSwitch.mixGains ?? {}) }, muted: { ...(audioSwitch.mixMuted ?? {}) }, monitorMuted: false });
    setShowMixer(true);
  };

  const validateMixer = () => {
    emitChange({ mode: 'mix', fixedAudioSourceId: '', mixGains: mixDraft.gains, mixMuted: mixDraft.muted });
    setShowMixer(false);
  };

  const handleFixedSourceChange = (id: string) => {
    emitChange({ mode: 'fixe', fixedAudioSourceId: id });
  };

  // ── Construction de la liste audio ───────────────────────────────────────
  // Sources distantes : TOUTES celles qui sont connectées (pas seulement dans le deck)
  // Périphériques système : liste fournie par le parent

  const remoteAudioOptions = useMemo(() =>
    Array.from(remoteConnections.entries()).map(([device_id, conn]) => {
      const device = remoteDevices.find(d => d.device_id === device_id);
      const label  = cleanLabel(device?.label ?? device_id) || device_id;
      return { id: `remote:${device_id}`, label };
    }),
    [remoteConnections, remoteDevices],
  );

  // On filtre les deviceId "étendus" (hls-audio-*, remote-audio-*, screen-audio-*)
  // qui ne sont pas de vrais périphériques système : ils sont construits plus bas
  // avec leur propre id d'option, et les inclure ici les proposerait deux fois.
  const deviceAudioOptions = useMemo(() =>
    availableAudioDevices
      .filter(d =>
        !d.deviceId.startsWith('hls-audio-') &&
        !d.deviceId.startsWith('remote-audio-') &&
        !d.deviceId.startsWith('screen-audio')
      )
      .map(d => ({
        id   : `dev:${d.deviceId}`,
        label: d.label || `Micro ${d.deviceId.slice(0, 8)}...`,
      })),
    [availableAudioDevices],
  );

  const screenAudioOptions = useMemo(() =>
    availableAudioDevices
      .filter(d => d.deviceId.startsWith('screen-audio'))
      .map(d => ({ id: `dev:${d.deviceId}`, label: d.label })),
    [availableAudioDevices],
  );

const hlsAudioOptions = useMemo(() =>
  availableAudioDevices
    .filter(d => d.deviceId.startsWith('hls-audio-'))
    .map(d => ({
      id   : `hls:${d.deviceId.replace('hls-audio-', '')}`,
      label: d.label,
    })),
  [availableAudioDevices],
);


  const hasAudioOptions = remoteAudioOptions.length > 0 || deviceAudioOptions.length > 0
                       || screenAudioOptions.length > 0 || hlsAudioOptions.length > 0;

  // Valeur affichée dans le select :
  // si la valeur courante existe dans la liste on la garde, sinon premier élément
  const allOptions = useMemo(
    () => [...remoteAudioOptions, ...deviceAudioOptions, ...screenAudioOptions, ...hlsAudioOptions],
    [remoteAudioOptions, deviceAudioOptions, screenAudioOptions, hlsAudioOptions],
  );

  const attachedSet = useMemo(
    () => (deckAttachedAudioIds ? new Set(deckAttachedAudioIds) : null),
    [deckAttachedAudioIds],
  );
  // Liste non fournie (mode non contrôlé) → on ne sait rien du deck, tout est
  // présenté comme rattaché plutôt que de bâtir un groupe "non rattaché" faux.
  const isDeckAttached = (id: string) => !attachedSet || attachedSet.has(id);

  const renderAudioOption = (o: { id: string; label: string }) => (
    <option key={o.id} value={o.id}>{o.label}</option>
  );

  // Seul le groupe "Périphériques audio" est scindé : un micro système peut être
  // affecté à un emplacement du deck ou non, et les deux cas ne se pilotent pas
  // pareil (le second est acquis à la demande par le parent). Les autres familles
  // gardent leur groupe d'origine.
  const attachedDeviceOptions = useMemo(
    () => deviceAudioOptions.filter(o => isDeckAttached(o.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceAudioOptions, attachedSet],
  );
  const detachedDeviceOptions = useMemo(
    () => deviceAudioOptions.filter(o => !isDeckAttached(o.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceAudioOptions, attachedSet],
  );

  const selectValue = useMemo(() => {
    if (fixedSourceId && allOptions.some(o => o.id === fixedSourceId)) return fixedSourceId;
    return allOptions[0]?.id ?? '';
  }, [fixedSourceId, allOptions]);

  const audioLabelStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 10, fontWeight: 700, letterSpacing: '0.4px',
    color: active ? '#FFFFFF' : 'rgba(148,163,184,0.4)',
    whiteSpace: 'nowrap', userSelect: 'none',
    transition: 'color 0.2s',
  });


  return (
    <>
      <div data-controlbar style={{
        display: 'flex', alignItems: 'stretch', padding: '0 10px', height: 76,
        background: 'var(--color-Panel-niv1-BG)', backdropFilter: 'blur(4px)',
        borderRadius: 10, border: '1px solid var(--color-Panel-niv1-Border)',
      }}>

        {/* ── DIFFUSION ── */}
        <Section label="Diffusion">
          {(() => {
            // Démarrer la diffusion exige la chaîne VRAIMENT active (channelFullyActive :
            // channelStatus === 'active', ni 'activating', ni 'deactivating', ni 'inactive').
            // Comme pour Rec, on ne bloque jamais l'arrêt d'une diffusion déjà en cours
            // (isStreamButtonActive) : sinon une chaîne qui se désactive pendant qu'on
            // diffuse rendrait le bouton Stop inaccessible.
            const canStartStreaming = channelFullyActive && hasCurrentStream && isRegiePowered;
            const inactive  = !isStreamButtonActive && !canStartStreaming;
            const disabled  = inactive || isStreamBusy;
            const bg = (isStreamButtonActive || isStartingStream)
                     ? '#dc2626'
                     : BTN.background as string;
            const anim = isStartingStream ? 'liveGlowRed 1s ease-in-out infinite'
                       : isStoppingStream ? 'liveGlowGrey 1s ease-in-out infinite'
                       : undefined;
            return (
              <button
                onClick={() => isStreamButtonActive ? handleStopStreaming() : onStartStreaming()}
                disabled={disabled}
                style={{
                  ...BTN,
                  background: bg,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  animation: anim,
                }}
              >
                {isStreamBusy
                  ? <AnimatedWaitingIcon />
                  : <div style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name="live" height={28} width={28} hoverable={false} clickable={false} />
                    </div>
                }
                On air
              </button>
            );
          })()}

          {(() => {
            // Même logique visuelle que le bouton Rec : gris au repos, rouge
            // dès qu'actif ou en cours d'activation, halo rouge pendant
            // l'activation, halo gris pendant la désactivation.
            const isActivatingMultiAngle   = multiAngleToggling && !multiAngleActive;
            const isDeactivatingMultiAngle = multiAngleToggling && multiAngleActive;
            const maAnim = isActivatingMultiAngle   ? 'liveGlowRed 1s ease-in-out infinite'
                         : isDeactivatingMultiAngle ? 'liveGlowGrey 1s ease-in-out infinite'
                         : undefined;
            return (
              <button
                onClick={handleToggleMultiAngle}
                disabled={multiAngleToggling}
                style={{
                  ...BTN,
                  background: (multiAngleActive || isActivatingMultiAngle) ? '#dc2626' : BTN.background as string,
                  cursor: multiAngleToggling ? 'not-allowed' : 'pointer',
                  animation: maAnim,
                }}
              >
                {multiAngleToggling
                  ? <AnimatedWaitingIcon />
                  : <Circle style={{ width: 16, height: 16, flexShrink: 0 }} />
                }
                Multi view
              </button>
            );
          })()}

          {/* Sous-titres en direct du flux final (2026-10-15). */}
          <SubtitlesControl broadcastingChannelId={broadcastingChannelId} buttonStyle={BTN} />

          <button
            onClick={() => setShowWhipEncodingSettings(true)}
            style={{ ...BTN_ICON }}
            title="Réglages d'encodage de la diffusion"
          >
            <Settings style={{ width: 15, height: 15 }} />
          </button>
        </Section>

        <Sep />

        {/* ── ENREGISTREMENT ── */}
        <Section label="Enregistrement">
          {(() => {
            // Même halo lumineux que le bouton Live régie : rouge pendant le démarrage,
            // gris pendant l'arrêt. isTogglingRecording seul ne distingue pas les deux
            // phases — mais isRecording, lui, ne bascule qu'APRES coup dans chaque cas
            // (cf. handleStartRecording/handleStopRecording), donc la combinaison des deux
            // les distingue proprement : toggling && !recording = démarrage, toggling && recording = arrêt.
			
      		// <><AnimatedWaitingIcon /><Circle style={{ width: 16, height: 16, flexShrink: 0 }} /></>

            const isStartingRecording = isTogglingRecording && !isRecording;
            const isStoppingRecording = isTogglingRecording && isRecording;
            const recAnim = isStartingRecording ? 'liveGlowRed 1s ease-in-out infinite'
                          : isStoppingRecording ? 'liveGlowGrey 1s ease-in-out infinite'
                          : undefined;
            return (
          <button
            onClick={() => isRecording ? onStopRecording() : onStartRecording()}
            disabled={recordingDisabled}
            title={!isRecording && !channelReadyForRecording ? 'Activez la chaîne pour enregistrer' : remainingRecordingSec != null ? `Quota restant : ${formatRemainingDuration(remainingRecordingSec)} d'enregistrement (estimation)` : undefined}
            style={{
              ...BTN,
              background: (isRecording || isStartingRecording || isStoppingRecording) ? '#dc2626'
                : BTN.background as string,
              border: BTN.border as string,
              color: '#fff',
              fontSize: 13, minWidth: isRecording ? 156 : 80,
              cursor: recordingDisabled ? 'not-allowed' : 'pointer',
              animation: recAnim,
            }}
          >
            {isTogglingRecording
              ? <><span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>Un instant...</span></>
              : isRecording
                ? <>
                    <span style={{ position: 'relative', display: 'flex', width: 12, height: 12, flexShrink: 0 }}>
                      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#fff', opacity: 0.75, animation: 'recPing 1s ease-in-out infinite' }} />
                      <span style={{ position: 'relative', display: 'inline-flex', borderRadius: '50%', width: 12, height: 12, background: '#fff' }} />
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatElapsed(recordingElapsedSec)}
                      {remainingRecordingSec != null && ` / ${formatElapsed(Math.max(0, Math.floor(remainingRecordingSec)))}`}
                    </span>
                  </>
                : <><Circle style={{ width: 16, height: 16, flexShrink: 0 }} />Rec</>}
          </button>
            );
          })()}

          <button onClick={onOpenStorage} style={{ ...BTN }} title="Videos enregistrees">
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 15, height: 15, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5a1.5 1.5 0 011.5-1.5h15A1.5 1.5 0 0121 7.5v1A1.5 1.5 0 0119.5 10h-15A1.5 1.5 0 013 8.5v-1z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18v7.5A1.5 1.5 0 0119.5 19h-15A1.5 1.5 0 013 17.5V10z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 14h6" />
            </svg>
            Stockage
          </button>
        </Section>

        <Sep />

        {/* ── CONFIGURATION ── */}
        <Section label="Configuration">
          <button onClick={onOpenPupitreConfig} style={{ ...BTN }}>
            <Settings style={{ width: 15, height: 15 }} />
            Pupitre
          </button>
          <button onClick={() => onOpenMediaboardConfig()} style={{ ...BTN }}>
            <Settings style={{ width: 15, height: 15 }} />
            Media et Scene
          </button>
        </Section>

        <Sep />

        {/* ── SOURCES EXTERNES ── */}
        <Section label="Sources externes">
          <div style={{
            height: 44, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '0 8px', gap: 1, flexShrink: 0,
          }}>
            <span style={{
              fontSize: 18, fontWeight: 800, fontFamily: 'monospace', lineHeight: 1,
              color: connectedCount > 0 ? '#ffffff' : '#ffffff',
            }}>
              {connectedCount}/{totalCount}
            </span>
            <span style={{ fontSize: 9, color: '#ffffff' }}>sources</span>
          </div>

          {(() => {
            // Même gating que « Live régie » : tant que la chaîne n'est pas réellement
            // démarrée, les paths MediaMtx (webrtc_/live_UUID) n'existent pas — une source
            // ajoutée ici n'aurait nulle part où se publier. Le titre est porté par le
            // <span> englobant : un <button disabled> n'affiche pas toujours son title.
            const addSourceDisabled = !channelFullyActive;
            return (
              <span
                title={addSourceDisabled
                  ? 'Activer la chaîne pour accéder à cette fonction'
                  : 'Ajouter une source externe'}
                style={{ display: 'flex', flexShrink: 0 }}
              >
                <button
                  onClick={() => setShowAddModal(true)}
                  disabled={addSourceDisabled}
                  style={{
                    ...BTN_ICON,
                    cursor : addSourceDisabled ? 'not-allowed' : 'pointer',
                    opacity: addSourceDisabled ? 0.45 : 1,
                  }}
                >
                  <Plus style={{ width: 15, height: 15 }} />
                </button>
              </span>
            );
          })()}

          <button
            onClick={() => setShowConfigModal(true)}
            style={{ ...BTN_ICON }}
            title="Gerer les sources externes"
          >
            <Settings style={{ width: 15, height: 15 }} />
          </button>
        </Section>

        <Sep />

        {/* ── RÉALISATEUR AUTO ── */}
        {onAutoDirectorToggle && autoDirectorSettings && onAutoDirectorSettingsChange && (
          <>
            <Section label="Réalisateur auto">
              {(() => {
                // 'paused' = l'opérateur vient de choisir lui-même une source ; 'blocked' =
                // ce poste n'a pas la main, la régie est hors tension ou une scène est au final.
                const statusText =
                  autoDirectorStatus === 'paused'  ? 'En pause (choix manuel)' :
                  autoDirectorStatus === 'blocked' ? 'Suspendu' : 'Actif';
                const title = autoDirectorEnabled
                  ? `Réalisateur auto : ${statusText}. Cliquer pour le couper.`
                  : "Activer le réalisateur auto : la source choisie selon les réglages passe seule au final";
                return (
                  <button
                    onClick={() => onAutoDirectorToggle(!autoDirectorEnabled)}
                    title={title}
                    style={{
                      ...BTN,
                      background: autoDirectorEnabled ? '#16a34a' : BTN.background as string,
                      border: autoDirectorEnabled ? '1px solid #22c55e' : BTN.border as string,
                      opacity: autoDirectorEnabled && autoDirectorStatus !== 'active' ? 0.75 : 1,
                    }}
                  >
                    <Clapperboard style={{ width: 15, height: 15 }} />
                    {autoDirectorEnabled
                      ? (autoDirectorStatus === 'active' ? 'Auto : ON' : autoDirectorStatus === 'paused' ? 'Auto : pause' : 'Auto : suspendu')
                      : 'Auto'}
                  </button>
                );
              })()}
              <button
                onClick={() => setShowAutoDirectorSettings(true)}
                style={{ ...BTN_ICON }}
                title="Réglages du réalisateur auto"
              >
                <Settings style={{ width: 15, height: 15 }} />
              </button>
            </Section>

            <Sep />
          </>
        )}

        {/* ── SWITCH AUDIO ── */}
        <Section label="Switch audio">
          <select
            value={audioMode}
            onChange={(e) => handleModeChange(e.target.value as AudioSwitchState['mode'])}
            title="Source : l'audio suit la source vidéo finale. Fixe : une seule source audio choisie. Mix : plusieurs sources mélangées au Mixeur."
            style={{
              height: 36, padding: '0 8px', borderRadius: 6,
              background: 'rgba(30,41,59,0.9)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#FFFFFF', fontSize: 11,
              fontFamily: 'monospace', fontWeight: 600,
              cursor: 'pointer', outline: 'none',
              flexShrink: 0, colorScheme: 'dark',
            }}
          >
            <option value="source">Source</option>
            <option value="fixe">Fixe</option>
            <option value="mix">Mix</option>
          </select>

          {audioMode === 'fixe' && (
            hasAudioOptions ? (
              <select
                value={selectValue}
                onChange={(e) => handleFixedSourceChange(e.target.value)}
                style={{
                  height: 36, padding: '0 8px', borderRadius: 6,
                  background: 'rgba(30,41,59,0.9)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#FFFFFF', fontSize: 11,
                  fontFamily: 'monospace', fontWeight: 600,
                  cursor: 'pointer', outline: 'none',
                  minWidth: 93, maxWidth: 147,
                  flexShrink: 0, colorScheme: 'dark',
                }}
              >
                {remoteAudioOptions.length > 0 && (
                  <optgroup label="Invites distants">
                    {remoteAudioOptions.map(renderAudioOption)}
                  </optgroup>
                )}
                {attachedDeviceOptions.length > 0 && (
                  <optgroup label="Périphériques audio rattachés au deck">
                    {attachedDeviceOptions.map(renderAudioOption)}
                  </optgroup>
                )}
                {detachedDeviceOptions.length > 0 && (
                  <optgroup label="Périphériques audio non rattachés au deck">
                    {detachedDeviceOptions.map(renderAudioOption)}
                  </optgroup>
                )}
                {screenAudioOptions.length > 0 && (
                  <optgroup label="Partages d'ecran">
                    {screenAudioOptions.map(renderAudioOption)}
                  </optgroup>
                )}
                {hlsAudioOptions.length > 0 && (
                  <optgroup label="Sources HLS">
                    {hlsAudioOptions.map(renderAudioOption)}
                  </optgroup>
                )}
              </select>
            ) : (
              <span style={{
                fontSize: 10, color: 'rgba(248,113,113,0.8)',
                fontStyle: 'italic', whiteSpace: 'nowrap',
              }}>
                Aucune source audio
              </span>
            )
          )}

          {/* Une source hors deck est parfaitement utilisable, mais elle est acquise
              pour le seul Switch audio : ni vignette, ni VU-mètre, ni réglages de
              pupitre. On le signale, sinon leur absence passe pour une panne. */}
          {audioMode === 'fixe' && hasAudioOptions && !isDeckAttached(selectValue) && (
            <span
              title="Cette source n'occupe aucun emplacement du deck : elle est acquise uniquement pour le Switch audio (pas de VU-mètre ni de réglages au pupitre)."
              style={{
                fontSize: 10, color: 'rgba(148,163,184,0.75)',
                fontStyle: 'italic', whiteSpace: 'nowrap',
              }}
            >
              hors deck
            </span>
          )}

          {audioMode === 'mix' && (
            <button
              ref={mixerBtnRef}
              onClick={() => (showMixer ? setShowMixer(false) : openMixer())}
              title="Régler le niveau de chaque source audio du mix"
              style={{ ...BTN, height: 36, ...(showMixer ? { borderColor: '#25C400' } : {}) }}
            >
              <SlidersHorizontal style={{ width: 14, height: 14 }} />
              Mixeur
            </button>
          )}

          {/* Unité de TOUS les VU-mètres de la régie (deck, mixeur, réglage des sources),
              mémorisée dans ce navigateur : dBFS crête (saturation) ou LUFS (volume perçu,
              EBU R128 Momentary, cible −14). */}
          <div role="radiogroup" aria-label="Unité des VU-mètres"
               style={{ display: 'flex', height: 36, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)' }}>
            {([['peak', 'dBFS', 'dBFS crête : le pic du signal, pour éviter la saturation (rouge au-delà de 0)'],
               ['lufs', 'LUFS', `LUFS : le volume perçu (EBU R128, 400 ms), autour de la cible ${LUFS_TARGET} LUFS du streaming`]] as const).map(([m, label, tip]) => (
              <button key={m} type="button" role="radio" aria-checked={vuMode === m} title={tip}
                      onClick={() => setVuMeterMode(m)}
                      style={{ padding: '0 10px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
                               background: vuMode === m ? '#25C400' : 'rgba(255,255,255,0.05)',
                               color:      vuMode === m ? '#0f172a' : 'rgba(255,255,255,0.7)' }}>
                {label}
              </button>
            ))}
          </div>
        </Section>


        <Sep />

        <div style={{ flex: 1 }} />

        {/* ── REGIE ── */}
        <Section label="Regie">
          <button onClick={onSaveConfig} style={{ ...BTN_ICON }} title="Sauvegarder la régie">
            <Save style={{ width: 15, height: 15 }} />
          </button>
          <button onClick={onLoadConfig} style={{ ...BTN_ICON }} title="Charger une régie">
            <FolderOpen style={{ width: 15, height: 15 }} />
          </button>
        </Section>
      </div>

      {audioMode === 'mix' && showMixer && (() => {
        const mixOptions = allOptions;
        const gains = mixDraft.gains;
        const muted = mixDraft.muted;
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)', padding: 16, boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                maxWidth: '95vw', maxHeight: '92vh', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                borderRadius: 16,
                background: 'var(--color-Panel-niv1-BG)', border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.85)', color: '#FFFFFF',
                fontFamily: 'monospace', fontSize: 11,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SlidersHorizontal style={{ width: 16, height: 16, opacity: 0.7 }} /> Mixeur
                </span>
                <span style={{ color: 'rgba(148,163,184,0.85)', fontSize: 10 }}>
                  Vous entendez les réglages en cours — ils ne passent à l'antenne qu'à la validation.
                </span>
              </div>
              <div style={{ padding: 20, overflow: 'auto', flex: 1, minHeight: 0 }}>
              {mixOptions.length === 0 ? (
                <span style={{ color: 'rgba(248,113,113,0.8)', fontStyle: 'italic' }}>
                  Aucune source audio détectée
                </span>
              ) : (
                // Voies verticales côte à côte (MixerStrip → HlsAudioPanel), remises le
                // 2026-10-18 après deux jours de voies horizontales.
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  {mixOptions.map(o => (
                    <MixerStrip
                      key={o.id}
                      label={o.label}
                      sourceId={mixSourceIds?.[o.id] ?? ''}
                      registry={mixMeterRegistry}
                      gain={gains[o.id] ?? 1}
                      muted={!!muted[o.id]}
                      onGainChange={g => handleMixGainChange(o.id, g)}
                      onMutedChange={m => handleMixMutedSet(o.id, m)}
                    />
                  ))}
                </div>
              )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                <button
                  onClick={() => setMixDraft(d => ({ ...d, monitorMuted: !d.monitorMuted }))}
                  title={mixDraft.monitorMuted ? "Réactiver l'écoute du brouillon" : "Couper l'écoute du brouillon (les réglages ne changent pas)"}
                  style={{ ...BTN, height: 36, ...(mixDraft.monitorMuted ? { color: '#fca5a5', borderColor: 'rgba(239,68,68,0.5)' } : {}) }}
                >
                  {mixDraft.monitorMuted ? <VolumeX style={{ width: 16, height: 16 }} /> : <Volume2 style={{ width: 16, height: 16 }} />}
                </button>
                <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button" onClick={() => setShowMixer(false)}
                  style={{
                    padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.75)',
                    transition: 'all 0.15s',
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button" onClick={validateMixer}
                  style={{
                    padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', border: 'none',
                    background: 'rgba(99,102,241,0.85)', color: '#ffffff',
                    transition: 'all 0.15s',
                  }}
                >
                  Valider
                </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Réglages du réalisateur auto ──────────────────────────────────────── */}
      {autoDirectorSettings && onAutoDirectorSettingsChange && (
        <AutoDirectorSettingsModal
          open={showAutoDirectorSettings}
          onClose={() => setShowAutoDirectorSettings(false)}
          settings={autoDirectorSettings}
          onApply={onAutoDirectorSettingsChange}
          candidates={autoDirectorCandidates}
        />
      )}

      {/* ── Réglages d'encodage de la diffusion ───────────────────────────────── */}
      <WhipEncodingSettingsModal
        open={showWhipEncodingSettings}
        onClose={() => setShowWhipEncodingSettings(false)}
        settings={whipEncoding.settings}
        onApply={whipEncoding.setSettings}
        orientation={programOrientation}
      />

      {/* ── Modales sources externes ──────────────────────────────────────────── */}
      <ProductionControlRoom_RemoteDevicesPanel
        mode="add"
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        remoteDevices={remoteDevices}
        remoteConnections={remoteConnections}
        serverUrl={serverUrl}
        streamPath={streamPath}
        webRtcPageUrl={webRtcPageUrl}
        channelName={channelName}
        microserviceUrl={microserviceUrl}
        onLoadDevices={onLoadDevices}
        onRegisterDevice={onRegisterDevice}
        onDeleteDevice={onDeleteDevice}
        onDisconnectDevice={onDisconnectDevice}
        onRegeneratePassword={onRegeneratePassword}
        onSetDirector={onSetDirector}
        directorDeviceIds={directorDeviceIds}
        deviceNetworkQuality={deviceNetworkQuality}
        onRegisterXtreamConnection={onRegisterXtreamConnection}
        sourceAliases={sourceAliases}
        onAliasesChange={onAliasesChange}
		availableSources={availableSources}
        hlsProbeStatusMap={hlsProbeStatusMap}
     selectedVideoSource={selectedVideoSource}
     deckPreviewSourceId={deckPreviewSourceId}
     onRemoveSource={onRemoveSource}	
     onReloadSource={onReloadSource}
     onRenameSource={onRenameSource}
       channelActive={channelActive}
	 
      />

      <ProductionControlRoom_RemoteDevicesPanel
        mode="manager"
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        remoteDevices={remoteDevices}
        remoteConnections={remoteConnections}
        serverUrl={serverUrl}
        streamPath={streamPath}
        webRtcPageUrl={webRtcPageUrl}
        channelName={channelName}
        microserviceUrl={microserviceUrl}
        onLoadDevices={onLoadDevices}
        onRegisterDevice={onRegisterDevice}
        onDeleteDevice={onDeleteDevice}
        onDisconnectDevice={onDisconnectDevice}
        onRegeneratePassword={onRegeneratePassword}
        onSetDirector={onSetDirector}
        directorDeviceIds={directorDeviceIds}
        deviceNetworkQuality={deviceNetworkQuality}
        onRegisterXtreamConnection={onRegisterXtreamConnection}
        sourceAliases={sourceAliases}
        onAliasesChange={onAliasesChange}
        availableSources={availableSources}
        hlsProbeStatusMap={hlsProbeStatusMap}		
     selectedVideoSource={selectedVideoSource}
     deckPreviewSourceId={deckPreviewSourceId}
     onRemoveSource={onRemoveSource}
     onReloadSource={onReloadSource}
     onRenameSource={onRenameSource}
channelActive={channelActive}
	 
      />
    </>
  );
};

export default ControlBar;